import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { PageLayoutMode } from './loader';
import { WorkerDviDocument } from './workerClient';

/** 前景色 #333 (global.css の --color-fg と揃える) */
const FG = 0x333333ff;
const SP_PER_PT = 65536;
/** ピンチズームで再ラスタライズする最大倍率 (メモリ保護) */
const MAX_PINCH_SCALE = 4;
/** 1 ページのラスタの最大辺 (px)。canvas 上限とメモリの保護 */
const MAX_PAGE_EDGE_PX = 8192;
/** 可視領域の上下このぶんだけ先読みラスタライズする */
const PREFETCH_MARGIN = '200% 0px';
/** この範囲から出たページはラスタを解放する (C4)。CSS 寸法は据え置きなので
    レイアウトは動かず、近づけば再ラスタライズされる */
const RELEASE_MARGIN = '600% 0px';
// ページめくりの見開き挙動は CSS 変数 --dvi-expand (0〜1) で制御する。
// 値は外側 (Article のビューワカード) がスクロール量から設定し、CSS が
// カード幅・スロット幅・左右ページのスライドを連続的に補間する (global.css)。

/**
 * テキストラン (sp) を透明テキスト層としてページラッパへ追加する (C2)。
 * 実測幅への水平スケールで字送りを合わせる (PDF.js の textLayer と同じ方式)。
 * リンクアンカーより DOM 順で前に挿入し、リンククリックを妨げない。
 */
function buildTextLayer(
  wrap: HTMLElement,
  runs: { x: number; y: number; s: number; w: number; t: string }[],
  cssPxPerPt: number,
) {
  wrap.querySelector('.dvi-text-layer')?.remove();
  if (runs.length === 0) {
    return;
  }
  const layer = document.createElement('div');
  layer.className = 'dvi-text-layer';
  const scale = cssPxPerPt / SP_PER_PT;
  const ctx = document.createElement('canvas').getContext('2d');
  for (const r of runs) {
    const span = document.createElement('span');
    span.textContent = r.t;
    const fontPx = r.s * scale;
    span.style.fontSize = `${fontPx}px`;
    span.style.left = `${r.x * scale}px`;
    // y はベースライン。おおよそのアセント (0.82em) ぶん上へ
    span.style.top = `${r.y * scale - fontPx * 0.82}px`;
    if (ctx) {
      ctx.font = `${fontPx}px serif`;
      const natural = ctx.measureText(r.t).width;
      if (natural > 0) {
        span.style.transform = `scaleX(${(r.w * scale) / natural})`;
      }
    }
    layer.appendChild(span);
  }
  wrap.insertBefore(layer, wrap.querySelector('a.dvi-link'));
}

/** リンク矩形 (sp) を透明な <a> としてページラッパへ追加する (C1)。 */
function appendLinkAnchors(
  wrap: HTMLElement,
  links: { x: number; y: number; w: number; h: number; href: string }[],
  linkScale: number,
  pad: number,
) {
  // 非同期到着どうしの競合に備え、直前に既存アンカーを払う
  wrap.querySelectorAll('a.dvi-link').forEach((el) => el.remove());
  for (const l of links) {
    const a = document.createElement('a');
    a.className = 'dvi-link';
    a.href = l.href;
    if (!l.href.startsWith('/')) {
      a.target = '_blank';
      a.rel = 'noreferrer';
    }
    a.style.left = `${l.x * linkScale - pad}px`;
    a.style.top = `${l.y * linkScale - pad}px`;
    a.style.width = `${l.w * linkScale + 2 * pad}px`;
    a.style.height = `${l.h * linkScale + 2 * pad}px`;
    wrap.appendChild(a);
  }
}

/**
 * devicePixelRatio を追跡する。ブラウザ拡大 (Ctrl +/-) や OS スケーリングの
 * 変更で値が変わるたびに再レンダリングし、拡大時のボケを防ぐ。
 */
function useDevicePixelRatio(): number {
  const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1);
  useEffect(() => {
    const mq = window.matchMedia(`(resolution: ${dpr}dppx)`);
    const onChange = () => setDpr(window.devicePixelRatio || 1);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [dpr]);
  return dpr;
}

/**
 * ピンチズーム倍率 (visualViewport.scale) を追跡する。ピンチ中は連続で
 * 発火するため 200ms デバウンスし、確定後に実効解像度で再ラスタライズする。
 * 非対応環境では 1。
 */
function useVisualViewportScale(): number {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }
    let timer = 0;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setScale(Math.min(Math.max(vv.scale, 1), MAX_PINCH_SCALE));
      }, 200);
    };
    vv.addEventListener('resize', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      window.clearTimeout(timer);
    };
  }, []);
  return scale;
}

export interface DviViewProps {
  src: string;
  /**
   * ページ座標基準 (spec/03)。既定は 'paper' (紙面完全再現)。
   * 'crop' は文書全ページの合併インク外接範囲 + cropMarginPt の統一クロップ。
   */
  layout?: PageLayoutMode;
  /** crop レイアウトの余白 (pt)。既定 8pt */
  cropMarginPt?: number;
  /**
   * 表示倍率。
   * - 'text' (既定): TeX 10pt = この要素の CSS 1em (本文と文字サイズが揃う)
   * - 'fit-width': ページ幅をコンテナ幅に合わせる (紙面の複数ページ記事向け)。
   *   コンテナ幅の変化には ResizeObserver で追従し再ラスタライズする
   */
  scaling?: 'text' | 'fit-width';
  /**
   * 複数ページの表示方法。
   * - 'scroll' (既定): 全ページを縦に並べ、可視領域 ± 2 画面ぶんだけを
   *   遅延ラスタライズする (スケルトンで寸法は先に確定しているため
   *   レイアウトは動かない)
   * - 'paged': ページ送り UI を付けたページめくり表示。fit-width かつ
   *   コンテナ幅が SPREAD_MIN_WIDTH 以上なら前後ページを含む 3 ページ
   *   見開き (机に書類を並べる体験)、狭ければ 1 ページ表示になる。
   *   前後ページのクリックと ←/→ キーでもめくれる
   */
  pager?: 'scroll' | 'paged';
  /**
   * コンテナ内での水平配置。
   * - 'center' (既定): 中央寄せ。はみ出す場合は描画対象と描画領域の
   *   水平中心線を一致させる (紙面は中央揃えで組まれることが多いため)
   * - 'left': 左詰め。環境単位の DVI 断片 (命題・証明など) を本文の
   *   流れに揃えて置く場合に使う。はみ出しは左端から
   */
  align?: 'center' | 'left';
  /**
   * ページの枠線。既定は false (なし)。紙面や図として独立して見せる
   * 場合にのみ true にする。
   */
  frame?: boolean;
  /**
   * ページの背景。
   * - 'transparent' (既定): ラスタは非インク部分が alpha=0 なので、
   *   z-index の意味で下にある要素の背景がそのまま透ける
   * - 'paper': 白背景 (紙面再現。インクは白地前提で組まれている)
   */
  background?: 'paper' | 'transparent';
  /**
   * ビルド時計測のページ数と寸法 (articles.json の dvi フィールド)。
   * 与えると DVI やフォントの取得を待たずにスケルトンのレイアウトが
   * 確定する (レンダリング前後でレイアウトが動かない)。指定する layout
   * での寸法であること (紙面記事は paper)。
   */
  sizeHint?: { pages: number; widthSp: number; heightSp: number };
  /**
   * paged のとき、現在ページ (1 始まり) を同期する URL クエリキー
   * (例 'page' → ?page=3)。リロードや共有で同じページが開く。
   * 1 ページ目ではキーを消して URL を汚さない。同一ページ内で複数の
   * DviView に同じキーを与えないこと
   */
  urlParam?: string;
}

interface Measured {
  pages: number;
  widthSp: number;
  heightSp: number;
}

/**
 * DVI をクライアントでラスタライズして表示する。
 *
 * レイアウト安定化 (spec/03): DVI は組版済みで寸法が表示前に確定するため、
 * メトリクス取得後すぐに CSS 寸法を固定したスケルトン canvas を配置し、
 * フォント取得・ラスタライズは同じ canvas に描き込む (以降 DOM は動かない)。
 * dpr・ピンチズーム・コンテナ幅・ページ切替による再描画も CSS 寸法据え置きで
 * 上書きする。scroll モードのラスタライズは可視領域近傍のみ遅延実行する。
 */
export function DviView({
  src,
  layout = 'paper',
  cropMarginPt = 8,
  scaling = 'text',
  pager = 'scroll',
  align = 'center',
  frame = false,
  background = 'transparent',
  sizeHint,
  urlParam,
}: DviViewProps) {
  const viewRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<WorkerDviDocument | null>(null);
  const centeredKeyRef = useRef('');
  const [error, setError] = useState<string | null>(null);
  const [measured, setMeasured] = useState<Measured | null>(sizeHint ?? null);
  const [facesReady, setFacesReady] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // 初期ページは URL クエリから (1 始まり → 0 始まり)
  const initialPage = (() => {
    if (!urlParam) {
      return 0;
    }
    const n = Number.parseInt(searchParams.get(urlParam) ?? '', 10);
    return Number.isFinite(n) && n >= 1 ? n - 1 : 0;
  })();
  const [page, setPage] = useState(initialPage);
  // 初回マウントは URL のページを尊重し、src 変更 (別文書) では 1 ページ目へ
  const initialPageRef = useRef(initialPage);
  // ページめくり時の中央スロットの実測幅 (ラスタ解像度・オーバーレイの基準)
  const [pagePx, setPagePx] = useState<number | null>(null);
  const [fitWidth, setFitWidth] = useState<number | null>(null);
  const dpr = useDevicePixelRatio();
  const pinchScale = useVisualViewportScale();

  // 文書のロード: DVI 取得 → メトリクス解決 → 寸法計測 → フォント本体解決
  useEffect(() => {
    let cancelled = false;
    // sizeHint (ビルド時計測) があればスケルトンを即時確定し、
    // 実測値 (同一のはず) で後から上書きする
    setMeasured(sizeHint ?? null);
    setFacesReady(false);
    setPage(initialPageRef.current);
    initialPageRef.current = 0;
    setError(null);
    (async () => {
      const doc = await WorkerDviDocument.open(src);
      if (cancelled) {
        doc.close();
        return;
      }
      docRef.current = doc;
      await doc.resolveFonts('metrics');
      if (cancelled) {
        return;
      }
      const { widthSp, heightSp } = await doc.pageSizeSp(layout, cropMarginPt);
      if (cancelled) {
        return;
      }
      setMeasured({ pages: doc.pageCount, widthSp, heightSp });
      await doc.resolveFonts('face');
      if (!cancelled) {
        setFacesReady(true);
      }
    })().catch((e: unknown) => {
      if (!cancelled) {
        setError(String(e));
      }
    });
    return () => {
      cancelled = true;
      docRef.current?.close();
      docRef.current = null;
    };
  }, [src, layout, cropMarginPt, sizeHint]);

  // URL のページ番号が実ページ数を超えていたら最終ページに丸める
  useEffect(() => {
    if (measured && page > measured.pages - 1) {
      setPage(Math.max(0, measured.pages - 1));
    }
  }, [measured, page]);

  // 現在ページを URL クエリへ反映する (1 ページ目はキーを消す)。
  // replace 遷移で履歴は汚さない
  useEffect(() => {
    if (!urlParam || pager !== 'paged') {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 0) {
          next.delete(urlParam);
        } else {
          next.set(urlParam, String(page + 1));
        }
        return next;
      },
      { replace: true },
    );
  }, [page, urlParam, pager, setSearchParams]);

  // fit-width: コンテナ幅を追跡
  useEffect(() => {
    if (scaling !== 'fit-width') {
      setFitWidth(null);
      return;
    }
    const el = viewRef.current;
    if (!el) {
      return;
    }
    const update = () => setFitWidth(Math.floor(el.clientWidth));
    update();
    // 幅アニメーション (カードの拡幅トランジション等) の途中で毎フレーム
    // 再ラスタライズしないよう、変化が落ち着いてから反映する
    let timer = 0;
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(update, 150);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [scaling]);

  // ページめくり (fit-width・複数ページ) は常に [前, 現在, 次] の 3 スロット
  // 構成。スロット幅と左右の可視性は --dvi-expand が CSS で連続制御するので、
  // 折り畳み時 (expand=0) は左右が幅 0 で隠れ、実質 1 ページ表示になる
  const row = pager === 'paged' && scaling === 'fit-width' && (measured?.pages ?? 0) > 1;

  // 表示倍率 (css px / pt)。row ではスロット幅が --dvi-expand で連続変化する
  // ため、実測 (中央スロットの clientWidth、デバウンス済み) を基準にする
  const fontSizePx = viewRef.current
    ? parseFloat(getComputedStyle(viewRef.current).fontSize) || 16
    : 16;
  const pageBoxWidth =
    fitWidth !== null && fitWidth > 0 ? (row ? (pagePx ?? fitWidth) : fitWidth) : null;
  const cssPxPerPt =
    scaling === 'fit-width' && pageBoxWidth && measured && measured.widthSp > 0
      ? pageBoxWidth / (measured.widthSp / SP_PER_PT)
      : fontSizePx / 10;

  // スケルトン配置 + 遅延ラスタライズ
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !measured) {
      return;
    }
    const widthPt = measured.widthSp / SP_PER_PT;
    const heightPt = measured.heightSp / SP_PER_PT;
    const cssW = widthPt * cssPxPerPt;
    const cssH = heightPt * cssPxPerPt;
    // 実効解像度 (dpr × ピンチ)。canvas 上限・メモリ保護のためクランプする
    const devicePxPerPt = Math.min(
      cssPxPerPt * dpr * pinchScale,
      MAX_PAGE_EDGE_PX / Math.max(widthPt, heightPt),
    );

    // ページめくりではスロット [前, 現在, 次] を並べる。文書の端で存在
    // しないスロットは寸法を保ったまま visibility で隠し、現在ページが
    // 常に中央に来るようにする
    const count = pager === 'paged' ? (row ? 3 : 1) : measured.pages;
    const slotPage = (i: number) => (pager === 'paged' ? (row ? page - 1 + i : page) : i);
    const slotValid = (i: number) => slotPage(i) >= 0 && slotPage(i) < measured.pages;
    container.classList.toggle('dvi-row', row);
    const existing = Array.from(container.children).filter(
      (el): el is HTMLDivElement =>
        el instanceof HTMLDivElement && el.classList.contains('dvi-page-wrap'),
    );
    const reuse = existing.length === count;
    if (!reuse) {
      container.replaceChildren();
    }
    const canvases: HTMLCanvasElement[] = [];
    const wraps: HTMLDivElement[] = [];
    for (let i = 0; i < count; i++) {
      const wrap = reuse ? existing[i] : document.createElement('div');
      if (!reuse) {
        wrap.className = 'dvi-page-wrap';
        container.appendChild(wrap);
      }
      let canvas = wrap.querySelector('canvas');
      if (!canvas) {
        canvas = document.createElement('canvas');
        wrap.appendChild(canvas);
      }
      if (row) {
        // スロット幅 (--dvi-expand で連続変化) に追従して中身も滑らかに
        // スケールするよう、canvas は % + aspect-ratio で寸法を決める
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.style.aspectRatio = `${cssW} / ${cssH}`;
      } else {
        canvas.style.width = `${cssW}px`;
        canvas.style.height = `${cssH}px`;
        canvas.style.aspectRatio = '';
      }
      // 未描画の canvas だけスケルトン表示 (再描画では前の絵を残す)。
      // canvas.width の既定値は 300 なので描画済み判定は dataset で行う
      canvas.className =
        canvas.dataset.rendered !== undefined ? 'dvi-page' : 'dvi-page dvi-skeleton';
      canvases.push(canvas);
      wraps.push(wrap);
    }

    // スロットの役割クラス・端スロットの隠蔽・前後ページのクリックめくり
    wraps.forEach((wrap, i) => {
      wrap.classList.toggle('dvi-slot-center', row && i === 1);
      wrap.classList.toggle('dvi-slot-side', row && i !== 1);
      wrap.style.visibility = slotValid(i) ? '' : 'hidden';
      const side = row && i !== 1 && slotValid(i);
      wrap.classList.toggle('dvi-side-page', side);
      wrap.onclick = side
        ? (e) => {
            if (e.target instanceof Element && e.target.closest('a')) {
              return; // ページ内リンクのクリックはめくりにしない
            }
            setPage(slotPage(i));
          }
        : null;
    });

    // リンクオーバーレイ (C1) と選択・検索用テキスト層 (C2)。どちらも
    // 視認を妨げないため、ページのラスタライズ完了後に遅延構築する
    // (弱い CPU では pageText/pageLinks が初回描画を秒単位で遅らせていた)。
    // 存在しないスロットの層だけ先に払う
    const attachOverlays = (wrap: HTMLDivElement, pageIndex: number) => {
      const doc = docRef.current;
      if (!doc) {
        return;
      }
      const linkScale = cssPxPerPt / SP_PER_PT;
      const pad = 1.5 * cssPxPerPt; // クリックしやすいよう 1.5pt 広げる
      void doc
        .pageLinks(pageIndex, layout, cropMarginPt)
        .then((links) => {
          if (!wrap.isConnected) {
            return;
          }
          appendLinkAnchors(wrap, links, linkScale, pad);
        })
        .catch(() => {});
      void doc
        .pageText(pageIndex, layout, cropMarginPt)
        .then((runs) => {
          if (!wrap.isConnected) {
            return;
          }
          buildTextLayer(wrap, runs, cssPxPerPt);
        })
        .catch(() => {});
    };
    wraps.forEach((wrap, i) => {
      if (!slotValid(i)) {
        wrap.querySelectorAll('a.dvi-link').forEach((el) => el.remove());
        wrap.querySelector('.dvi-text-layer')?.remove();
      }
    });

    // 水平方向にはみ出す場合、描画対象の中心線と描画領域の中心線を一致させる
    // (本文は紙面中央に組まれることが多いため)。ユーザーのスクロールを
    // 上書きしないよう、寸法が決まった時点で一度だけ行う。
    // align='left' では左端から表示する (scrollLeft は既定の 0 のまま)。
    const scroll = scrollRef.current;
    if (scroll && align === 'center') {
      const key = `${src}:${layout}:${cropMarginPt}:${cssW.toFixed(2)}`;
      if (centeredKeyRef.current !== key) {
        centeredKeyRef.current = key;
        scroll.scrollLeft = Math.max(0, (scroll.scrollWidth - scroll.clientWidth) / 2);
      }
    }

    // row: 中央スロットの実測幅を追跡し、変化が落ち着いたらラスタ解像度と
    // オーバーレイの基準 (pagePx) を更新する
    let roCleanup: (() => void) | undefined;
    if (row) {
      const target = wraps[1];
      let t = 0;
      const ro = new ResizeObserver(() => {
        window.clearTimeout(t);
        t = window.setTimeout(() => {
          const w = Math.round(target.clientWidth);
          if (w > 0) {
            setPagePx((prev) => (prev === w ? prev : w));
          }
        }, 150);
      });
      ro.observe(target);
      roCleanup = () => {
        ro.disconnect();
        window.clearTimeout(t);
      };
    }

    if (!facesReady) {
      return roCleanup; // フォント待ち: スケルトンのまま (寸法は確定済み)
    }
    const renderKey = `${src}:${layout}:${cropMarginPt}:${devicePxPerPt.toFixed(4)}`;
    // ラスタライズは Worker で行い (C3)、結果のピクセルを描き込むだけ。
    // await 中の再入は dataset.pending の鍵で防ぐ
    const renderInto = (canvas: HTMLCanvasElement, pageIndex: number) => {
      const doc = docRef.current;
      const stamp = `${renderKey}#${pageIndex}`;
      if (!doc || canvas.dataset.rendered === stamp || canvas.dataset.pending === stamp) {
        return;
      }
      canvas.dataset.pending = stamp;
      doc
        .renderPage(pageIndex, devicePxPerPt, layout, cropMarginPt, FG)
        .then((image) => {
          if (canvas.dataset.pending !== stamp) {
            return; // 倍率変更などで別の描画が予約された
          }
          delete canvas.dataset.pending;
          canvas.width = image.width;
          canvas.height = image.height;
          canvas.getContext('2d')?.putImageData(image, 0, 0);
          canvas.classList.remove('dvi-skeleton');
          canvas.dataset.rendered = stamp;
          const wrap = canvas.parentElement;
          if (wrap instanceof HTMLDivElement) {
            attachOverlays(wrap, pageIndex);
          }
        })
        .catch((e: unknown) => {
          delete canvas.dataset.pending;
          setError(String(e));
        });
    };

    if (pager === 'paged') {
      canvases.forEach((canvas, i) => {
        if (slotValid(i)) {
          renderInto(canvas, slotPage(i));
        }
      });
      return roCleanup;
    }
    // scroll: 可視領域 ± 2 画面ぶんに入ったページだけラスタライズする。
    // 倍率が変わった場合も、observer の初期通知で可視ページから順に再描画される
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const canvas = entry.target as HTMLCanvasElement;
            renderInto(canvas, Number(canvas.dataset.page));
          }
        }
      },
      { rootMargin: PREFETCH_MARGIN },
    );
    // 遠ざかったページのラスタは解放する (長い紙面記事のメモリ保護)。
    // 寸法固定の CSS はそのままなのでレイアウトは動かない
    const release = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            continue;
          }
          const canvas = entry.target as HTMLCanvasElement;
          if (canvas.dataset.rendered !== undefined || canvas.dataset.pending !== undefined) {
            delete canvas.dataset.rendered;
            delete canvas.dataset.pending;
            canvas.width = 0;
            canvas.height = 0;
            canvas.classList.add('dvi-skeleton');
          }
        }
      },
      { rootMargin: RELEASE_MARGIN },
    );
    canvases.forEach((canvas, i) => {
      canvas.dataset.page = String(i);
      io.observe(canvas);
      release.observe(canvas);
    });
    return () => {
      io.disconnect();
      release.disconnect();
    };
  }, [
    src,
    measured,
    facesReady,
    cssPxPerPt,
    dpr,
    pinchScale,
    layout,
    cropMarginPt,
    pager,
    row,
    page,
    align,
  ]);

  const showPager = pager === 'paged' && measured !== null && measured.pages > 1;
  const viewClass = [
    'dvi-view',
    align === 'left' && 'dvi-align-left',
    frame && 'dvi-framed',
    background === 'paper' && 'dvi-paper',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={viewClass}
      ref={viewRef}
      tabIndex={showPager ? 0 : undefined}
      onKeyDown={
        showPager
          ? (e) => {
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setPage((p) => Math.max(0, p - 1));
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setPage((p) => Math.min((measured?.pages ?? 1) - 1, p + 1));
              }
            }
          : undefined
      }
    >
      {error && <p className="dvi-status dvi-error">表示に失敗しました: {error}</p>}
      {showPager && (
        <div className="dvi-pager">
          <button disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>
            ‹ 前のページ
          </button>
          <span>
            {page + 1} / {measured.pages}
          </span>
          <button disabled={page >= measured.pages - 1} onClick={() => setPage((p) => p + 1)}>
            次のページ ›
          </button>
        </div>
      )}
      <div className={`dvi-scroll${row ? ' dvi-scroll-clip' : ''}`} ref={scrollRef}>
        <div ref={containerRef} />
      </div>
    </div>
  );
}
